import { useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { KeyRound, Link2, Plus, ShieldCheck, UserMinus, UserPlus } from "lucide-react"
import { RollenTab, type RollenAntwort } from "@/components/zugaenge/RollenTab"
import { ZweiFaktorDialog, type ZweiFaktorGeheimnis } from "@/components/zugaenge/ZweiFaktorDialog"
import { EinladungDialog, type Einladung } from "@/components/zugaenge/EinladungDialog"

/**
 * Zugänge zum Portal verwalten (#27, Nachtrag 25.08.2026).
 *
 * Bis heute früh ging Anlegen, Deaktivieren und Rolle-Setzen nur per SQL auf
 * dem Server, und hinter der Rolle steckte kein Rechtekonzept — der zweite
 * Reiter ordnet den Rollen jetzt zu, was sie dürfen.
 *
 * 🔴 Alle Schutzregeln sitzen im Backend, nicht hier: kein Löschen, niemand
 * ändert sich selbst, der letzte Verwalter bleibt, die Rechte des
 * Administrators sind nicht abwählbar. Diese Seite blendet die entsprechenden
 * Knöpfe nur aus — sie ist Bequemlichkeit, nicht die Sperre.
 */

type Nutzer = {
  id: number
  username: string
  is_active: boolean
  rolle: string
  rolle_name?: string
  hat_2fa?: boolean
  einladung_bis?: string | null
  created_at: string
}

type Antwort = {
  items: Nutzer[]
  ich: string
  env_admin: string
  min_laenge: number
}

export default function ZugaengePage() {
  const qc = useQueryClient()
  const [reiter, setReiter] = useState<"zugaenge" | "rollen">("zugaenge")
  const [neuName, setNeuName] = useState("")
  const [neuPass, setNeuPass] = useState("")
  const [neuRolle, setNeuRolle] = useState("mitarbeiter")
  const [neuMail, setNeuMail] = useState("")
  const [geheimnis, setGeheimnis] = useState<ZweiFaktorGeheimnis | null>(null)
  const [einladung, setEinladung] = useState<Einladung | null>(null)
  const [meldung, setMeldung] = useState<{ art: "ok" | "fehler"; text: string } | null>(null)
  const melde = (art: "ok" | "fehler", text: string) => setMeldung({ art, text })

  const q = useQuery<Antwort>({
    queryKey: ["portal-users"],
    queryFn: async () => {
      const r = await fetch("/api/v1/portal-users", { credentials: "include" })
      if (!r.ok) throw new Error(r.status === 403
        ? "Dafür fehlt die Berechtigung „Zugänge und Rollen“."
        : `HTTP ${r.status}`)
      return r.json()
    },
  })
  // Die Auswahlliste kommt aus derselben Quelle wie der Reiter „Rollen" —
  // sonst steht hier irgendwann eine Rolle, die es nicht mehr gibt.
  const rollenQ = useQuery<RollenAntwort>({
    queryKey: ["rollen"],
    queryFn: async () => {
      const r = await fetch("/api/v1/rollen", { credentials: "include" })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      return r.json()
    },
  })
  const rollen = rollenQ.data?.rollen ?? []
  const minLaenge = q.data?.min_laenge ?? 12

  const frisch = () => qc.invalidateQueries({ queryKey: ["portal-users"] })
  const fehler = (e: unknown) =>
    melde("fehler", e instanceof Error ? e.message : String(e))

  const anlegen = useMutation({
    mutationFn: async () => {
      const r = await fetch("/api/v1/portal-users", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        // Ohne Passwort wartet der Zugang auf seinen Einladungslink (02.11.2026).
        body: JSON.stringify({ username: neuName, passwort: neuPass || null, rolle: neuRolle }),
      })
      const d = await r.json().catch(() => ({}))
      if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`)
      const nutzer = d as Nutzer
      if (neuPass) return { nutzer, link: null }
      // 🔑 Der Link wird hier im selben Zug geholt, nicht über `einladen.mutate`
      // aus onSuccess heraus: so verkettet, ging der Dialog beim Test auf dev
      // nie auf, obwohl die Einladung angelegt war.
      const e = await fetch(`/api/v1/portal-users/${encodeURIComponent(nutzer.username)}/einladung`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: neuMail.trim() || null }),
      })
      const link = await e.json().catch(() => ({}))
      if (!e.ok) throw new Error(`Zugang angelegt, aber kein Link: ${link.error || `HTTP ${e.status}`}`)
      return { nutzer, link: link as Einladung }
    },
    onSuccess: ({ nutzer: d, link }) => {
      setNeuName(""); setNeuPass(""); setNeuMail("")
      if (link) {
        melde("ok", `Zugang „${d.username}" angelegt.`)
        setEinladung(link)
      } else {
        melde("ok", `Zugang „${d.username}" angelegt. Zweiten Faktor einrichten nicht vergessen.`)
      }
      frisch()
      qc.invalidateQueries({ queryKey: ["rollen"] })
    },
    onError: (e) => { fehler(e); frisch() },
  })

  const aendern = useMutation({
    mutationFn: async (a: { username: string; body: Record<string, unknown>; was: string }) => {
      const r = await fetch(`/api/v1/portal-users/${encodeURIComponent(a.username)}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(a.body),
      })
      const d = await r.json().catch(() => ({}))
      if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`)
      return { ...a, d }
    },
    onSuccess: (r) => {
      melde("ok", r.was)
      frisch()
      qc.invalidateQueries({ queryKey: ["rollen"] })
    },
    onError: fehler,
  })

  // 2FA erneuern statt lösen (25.08.2026): am 25.08. verlor Robin auf dem alten
  // Weg seinen zweiten Faktor — der Zugang lief ungeschützt weiter, und nichts
  // erinnerte daran. Hier kommt ein frisches Geheimnis heraus, das man weitergibt.
  const zweiFa = useMutation({
    mutationFn: async (username: string) => {
      const r = await fetch(`/api/v1/portal-users/${encodeURIComponent(username)}/2fa`, {
        method: "POST", credentials: "include",
      })
      const d = await r.json().catch(() => ({}))
      if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`)
      return d as ZweiFaktorGeheimnis
    },
    onSuccess: (d) => { setGeheimnis(d); setMeldung(null); frisch() },
    onError: fehler,
  })

  // Einladungslink statt Passwort per Chat (02.11.2026): die Person setzt
  // Passwort und zweiten Faktor selbst. Geht auch für bestehende Zugänge.
  const einladen = useMutation({
    mutationFn: async ({ username, email }: { username: string; email: string }) => {
      const r = await fetch(`/api/v1/portal-users/${encodeURIComponent(username)}/einladung`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim() || null }),
      })
      const d = await r.json().catch(() => ({}))
      if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`)
      return d as Einladung
    },
    onSuccess: (d) => { setEinladung(d); frisch() },
    onError: fehler,
  })

  const passwortSetzen = (username: string) => {
    const neu = window.prompt(
      `Neues Passwort für „${username}“ (mindestens ${minLaenge} Zeichen).\n\n` +
      `Es gilt sofort. Sag es der Person auf einem Weg, auf dem es nicht stehen bleibt.`,
    )
    if (!neu) return
    if (neu.length < minLaenge) {
      melde("fehler", `Zu kurz — mindestens ${minLaenge} Zeichen.`)
      return
    }
    aendern.mutate({ username, body: { passwort: neu }, was: `Passwort für „${username}" gesetzt.` })
  }

  // Passwort leer lassen = Einladungslink; wer eins einträgt, braucht die Mindestlänge.
  const passtNeu = !!neuName.trim() && (neuPass === "" || neuPass.length >= minLaenge)
  const items = q.data?.items ?? []
  const ich = q.data?.ich ?? ""
  const feld = {
    fontSize: 13, padding: "6px 9px", borderRadius: 6,
    border: "1px solid var(--border-color)", background: "var(--bg-card)",
    color: "var(--text)",
  } as const
  const reiterStil = (aktiv: boolean) => ({
    fontSize: 13, fontWeight: aktiv ? 600 : 400, padding: "6px 2px",
    marginRight: 18, background: "none", border: "none", cursor: "pointer",
    color: aktiv ? "var(--text)" : "var(--text-muted)",
    borderBottom: aktiv ? "2px solid #3C3489" : "2px solid transparent",
  } as const)

  return (
    <div style={{ padding: "20px 24px", maxWidth: 820 }}>
      <h1 style={{ fontSize: 20, fontWeight: 600, marginBottom: 10 }}>Zugänge</h1>

      <div style={{ borderBottom: "1px solid var(--border-color)", marginBottom: 16 }}>
        <button type="button" style={reiterStil(reiter === "zugaenge")}
                onClick={() => { setReiter("zugaenge"); setMeldung(null) }}>
          Wer darf hinein
        </button>
        <button type="button" style={reiterStil(reiter === "rollen")}
                onClick={() => { setReiter("rollen"); setMeldung(null) }}>
          Rollen &amp; Rechte
        </button>
      </div>

      {reiter === "rollen" ? (
        <RollenTab melde={melde} />
      ) : (
      <>
      <p style={{ fontSize: 12.5, color: "var(--text-muted)", marginBottom: 18, maxWidth: 620 }}>
        Wer sich am Portal anmelden darf und was er sehen kann. <strong>Gelöscht
        wird nicht</strong> — wer geht, wird deaktiviert; sonst verlieren
        Aktivitätsprotokoll und Vorgangstexte ihren Bezug.
        {q.data?.env_admin && (
          <> Der Zugang „{q.data.env_admin}" steht in der Serverkonfiguration und
          taucht hier nicht auf.</>
        )}
      </p>

      {q.isError ? (
        <div style={{ fontSize: 13, color: "var(--destructive)" }}>{String(q.error)}</div>
      ) : q.isLoading ? (
        <div style={{ fontSize: 13, color: "var(--text-muted)" }}>Lädt …</div>
      ) : (
        <>
          <form
            onSubmit={(e) => {
              e.preventDefault(); setMeldung(null)
              if (passtNeu) anlegen.mutate()
            }}
            style={{
              display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center",
              marginBottom: 18, padding: 12, borderRadius: 8,
              border: "1px solid var(--border-color)", background: "var(--bg-card)",
            }}
          >
            <input value={neuName} onChange={(e) => setNeuName(e.target.value)}
                   placeholder="Benutzername" style={{ ...feld, flex: "1 1 160px" }} />
            <input value={neuPass} onChange={(e) => setNeuPass(e.target.value)}
                   type="password" autoComplete="new-password"
                   placeholder="Passwort leer lassen → Einladungslink"
                   title={`Leer lassen: Die Person bekommt einen Link und setzt ihr Passwort selbst. Sonst mindestens ${minLaenge} Zeichen.`}
                   style={{ ...feld, flex: "1 1 180px" }} />
            {!neuPass && (
              <input value={neuMail} onChange={(e) => setNeuMail(e.target.value)}
                     type="email" autoComplete="off"
                     placeholder="E-Mail für die Einladung (optional)"
                     title="Ausgefüllt: Der Link geht von kontakt@werkxpartner.de an diese Adresse. Leer: du schickst ihn selbst."
                     style={{ ...feld, flex: "1 1 200px" }} />
            )}
            <select value={neuRolle} onChange={(e) => setNeuRolle(e.target.value)} style={feld}>
              {/* 🔑 Die vorausgewählte Rolle braucht einen eigenen <option> —
                  sonst zeigt das Feld den Platzhalter, solange die Liste lädt. */}
              {!rollen.some((r) => r.schluessel === neuRolle) && (
                <option value={neuRolle}>{neuRolle}</option>
              )}
              {rollen.map((r) => (
                <option key={r.schluessel} value={r.schluessel}>{r.name}</option>
              ))}
            </select>
            <button
              type="submit"
              disabled={!passtNeu || anlegen.isPending}
              style={{
                display: "inline-flex", alignItems: "center", gap: 5, fontSize: 13,
                fontWeight: 500, padding: "6px 12px", borderRadius: 6, border: "none",
                background: passtNeu ? "#3C3489" : "rgba(44,44,42,0.12)",
                color: passtNeu ? "#fff" : "var(--text-muted)",
                cursor: passtNeu ? "pointer" : "not-allowed",
              }}
            >
              <UserPlus size={14} /> {neuPass ? "Anlegen" : neuMail.trim() ? "Anlegen & Mail schicken" : "Anlegen & einladen"}
            </button>
          </form>

          <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
            {items.map((n) => {
              const selbst = n.username === ich
              return (
                <li key={n.id} style={{
                  display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
                  padding: "10px 4px", borderBottom: "1px solid var(--border-color)",
                  opacity: n.is_active ? 1 : 0.55,
                }}>
                  <div style={{ flex: "1 1 200px", minWidth: 0 }}>
                    <div style={{ fontSize: 13.5, fontWeight: 500 }}>
                      {n.username}
                      {selbst && (
                        <span style={{ fontSize: 11.5, fontWeight: 400, color: "var(--text-muted)" }}>
                          {" "}· das bist du
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>
                      {n.rolle_name || n.rolle}
                      {!n.is_active && " · deaktiviert"}
                      {n.hat_2fa
                        ? " · 2FA aktiv"
                        : " · ohne zweiten Faktor"}
                      {n.einladung_bis && n.is_active && (
                        <> · Einladung offen bis {new Date(n.einladung_bis).toLocaleString("de-DE", {
                          day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit",
                        })}</>
                      )}
                    </div>
                  </div>

                  {selbst ? (
                    // Regel „niemand ändert sich selbst" sitzt im Backend; hier
                    // nur die Erklärung, warum nichts anzuklicken ist.
                    <span style={{ fontSize: 11.5, color: "var(--text-muted)" }}>
                      Eigenen Zugang unter „Mein Konto" ändern
                    </span>
                  ) : (
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                      <select
                        value={n.rolle}
                        onChange={(e) => aendern.mutate({
                          username: n.username, body: { rolle: e.target.value },
                          was: `„${n.username}" hat jetzt die Rolle „${
                            rollen.find((r) => r.schluessel === e.target.value)?.name
                            || e.target.value}".`,
                        })}
                        style={{ ...feld, fontSize: 12, padding: "4px 7px" }}
                      >
                        {!rollen.some((r) => r.schluessel === n.rolle) && (
                          <option value={n.rolle}>{n.rolle_name || n.rolle}</option>
                        )}
                        {rollen.map((r) => (
                          <option key={r.schluessel} value={r.schluessel}>{r.name}</option>
                        ))}
                      </select>
                      <button
                        type="button"
                        disabled={!n.is_active || einladen.isPending}
                        title="Link erzeugen, über den die Person Passwort und 2FA selbst setzt"
                        onClick={() => {
                          const email = window.prompt(
                            `E-Mail-Adresse von „${n.username}“ — der Link geht dann von kontakt@werkxpartner.de raus.\n\n` +
                            `Leer lassen, wenn du ihn selbst schicken willst.`,
                          )
                          if (email === null) return
                          einladen.mutate({ username: n.username, email })
                        }}
                        style={{ ...feld, fontSize: 12, padding: "4px 8px",
                                 cursor: n.is_active ? "pointer" : "not-allowed",
                                 opacity: n.is_active ? 1 : 0.5,
                                 display: "inline-flex", alignItems: "center", gap: 4 }}>
                        <Link2 size={12} /> Einladungslink
                      </button>
                      <button type="button" title="Passwort setzen"
                              onClick={() => passwortSetzen(n.username)}
                              style={{ ...feld, fontSize: 12, padding: "4px 8px", cursor: "pointer",
                                       display: "inline-flex", alignItems: "center", gap: 4 }}>
                        <KeyRound size={12} /> Passwort
                      </button>
                      <button
                        type="button"
                        disabled={!n.is_active || zweiFa.isPending}
                        title={n.hat_2fa
                          ? "Neues Geheimnis erzeugen — der alte Code gilt dann nicht mehr"
                          : "Zweiten Faktor einrichten und den Schlüssel weitergeben"}
                        onClick={() => zweiFa.mutate(n.username)}
                        style={{ ...feld, fontSize: 12, padding: "4px 8px",
                                 cursor: n.is_active ? "pointer" : "not-allowed",
                                 opacity: n.is_active ? 1 : 0.5,
                                 display: "inline-flex", alignItems: "center", gap: 4 }}>
                        <ShieldCheck size={12} /> {n.hat_2fa ? "2FA erneuern" : "2FA einrichten"}
                      </button>
                      <button type="button"
                              onClick={() => aendern.mutate({
                                username: n.username, body: { is_active: !n.is_active },
                                was: n.is_active
                                  ? `„${n.username}" deaktiviert.`
                                  : `„${n.username}" wieder aktiv.`,
                              })}
                              style={{ ...feld, fontSize: 12, padding: "4px 8px", cursor: "pointer",
                                       color: n.is_active ? "var(--destructive)" : "var(--text)",
                                       display: "inline-flex", alignItems: "center", gap: 4 }}>
                        {n.is_active ? <><UserMinus size={12} /> Deaktivieren</> : <><Plus size={12} /> Aktivieren</>}
                      </button>
                    </div>
                  )}
                </li>
              )
            })}
          </ul>
        </>
      )}
      </>
      )}

      {meldung && (
        <div style={{
          marginTop: 14, fontSize: 12.5,
          color: meldung.art === "ok" ? "#059669" : "var(--destructive)",
        }}>
          {meldung.text}
        </div>
      )}

      {einladung && (
        <EinladungDialog daten={einladung} onClose={() => setEinladung(null)} />
      )}

      {geheimnis && (
        <ZweiFaktorDialog daten={geheimnis} onClose={() => setGeheimnis(null)} />
      )}
    </div>
  )
}
